import React from 'react';
import { User, Loader2, Heart } from 'lucide-react';
import DatePicker from '@components/common/DatePicker';
import TimePicker from '@components/common/TimePicker';
import LocationPicker from '@components/common/LocationPicker';
import Select from '@components/common/Select';
import KundliSelector from './KundliSelector';
import { getLocalISODate, normalizeKundliDobToISO } from '@utils/dateUtils';

interface PersonData {
    name: string;
    dob: string;
    birth_time: string;
    location: string;
    latitude: number;
    longitude: number;
    gender: string;
}

interface CompatibilityFormProps {
    person1: PersonData;
    setPerson1: (data: PersonData) => void;
    person2: PersonData;
    setPerson2: (data: PersonData) => void;
    handleGenerate: () => void;
    loading: boolean;
    language: 'english' | 'hindi';
    selectedKundli1?: any;
    setSelectedKundli1?: (kundli: any) => void;
    selectedKundli2?: any;
    setSelectedKundli2?: (kundli: any) => void;
    translations: any;
}

const CompatibilityForm: React.FC<CompatibilityFormProps> = ({
    person1,
    setPerson1,
    person2,
    setPerson2,
    handleGenerate,
    loading,
    language,
    selectedKundli1,
    setSelectedKundli1,
    selectedKundli2,
    setSelectedKundli2,
    translations: t
}) => {
    const handleSelectKundli = (
        kundli: any,
        isCloud: boolean,
        setPerson: (data: PersonData) => void,
        setSelectedKundli?: (kundli: any) => void
    ) => {
        const normalizedDob = normalizeKundliDobToISO(kundli.dob);

        setPerson({
            name: kundli.name,
            gender: kundli.gender,
            dob: normalizedDob,
            birth_time: kundli.birth_time,
            location: kundli.birth_location || kundli.location || '',
            latitude: kundli.latitude,
            longitude: kundli.longitude
        });

        if (setSelectedKundli) {
            setSelectedKundli(kundli);
        }
    };

    const handleClearKundli = (
        setPerson: (data: PersonData) => void,
        defaultGender: string,
        setSelectedKundli?: (kundli: any) => void
    ) => {
        setPerson({
            name: '',
            dob: getLocalISODate(),
            birth_time: '12:00',
            location: '',
            latitude: 28.6139,
            longitude: 77.2090,
            gender: defaultGender
        });

        if (setSelectedKundli) {
            setSelectedKundli(null);
        }
    };

    const renderPersonForm = (
        person: PersonData,
        setPerson: (data: PersonData) => void,
        label: string,
        personNumber: 1 | 2,
        selectedKundli: any,
        setSelectedKundli?: (kundli: any) => void
    ) => (
        <div className="bg-white/[0.05] backdrop-blur-xl border border-white/10 rounded-[20px] p-5 md:p-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${personNumber === 1 ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-pink-500 to-pink-600'} flex items-center justify-center`}>
                    <User size={16} className="text-white" />
                </div>
                {label}
            </h3>

            {/* Kundli Selector */}
            <KundliSelector
                selectedKundli={selectedKundli}
                onSelect={(kundli, isCloud) => handleSelectKundli(kundli, isCloud, setPerson, setSelectedKundli)}
                onClear={() => handleClearKundli(setPerson, personNumber === 1 ? 'Male' : 'Female', setSelectedKundli)}
                label={label}
                language={language}
                translations={t}
            />

            <div className="space-y-5">
                {/* Name and Gender */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-2 ml-1">
                            {t.fullName}
                        </label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400">
                                <User size={16} />
                            </div>
                            <input
                                type="text"
                                placeholder={t.fullNamePlaceholder}
                                className="w-full bg-white/5 border border-white/20 rounded-2xl py-3 md:py-4 pl-11 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-colors placeholder:text-white/20 font-sans text-sm md:text-base"
                                value={person.name}
                                onChange={(e) => setPerson({ ...person, name: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-2 ml-1">
                            {t.gender}
                        </label>
                        <Select
                            value={person.gender}
                            onChange={(val) => setPerson({ ...person, gender: val })}
                            options={[
                                { value: 'Male', label: t.male },
                                { value: 'Female', label: t.female }
                            ]}
                        />
                    </div>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-2 ml-1">
                            {t.dateOfBirth}
                        </label>
                        <DatePicker
                            value={person.dob}
                            onChange={(val) => setPerson({ ...person, dob: val })}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-2 ml-1">
                            {t.timeOfBirth}
                        </label>
                        <TimePicker
                            value={person.birth_time}
                            onChange={(val) => setPerson({ ...person, birth_time: val })}
                        />
                    </div>
                </div>

                {/* Location */}
                <div>
                    <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-2 ml-1">
                        {t.birthLocation}
                    </label>
                    <LocationPicker
                        value={person.location}
                        placeholder={t.searchCity}
                        onChange={(loc) => setPerson({
                            ...person,
                            location: loc.name,
                            latitude: loc.latitude,
                            longitude: loc.longitude
                        })}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 mb-10 md:mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {renderPersonForm(person1, setPerson1, t.person1, 1, selectedKundli1, setSelectedKundli1)}
                {renderPersonForm(person2, setPerson2, t.person2, 2, selectedKundli2, setSelectedKundli2)}
            </div>

            <div className="text-center">
                <button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="group relative px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-black rounded-2xl transition-colors shadow-[0_10px_30px_rgba(219,39,119,0.3)] hover:shadow-[0_15px_40px_rgba(219,39,119,0.4)] disabled:opacity-50 flex items-center gap-3 overflow-hidden mx-auto"
                >
                    <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                    {loading ? (
                        <div className="flex items-center gap-3">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t.analyzing}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3">
                            <Heart size={20} />
                            <span className="uppercase tracking-widest">{t.checkCompatibility}</span>
                        </div>
                    )}
                </button>
            </div>
        </div>
    );
};

export default CompatibilityForm;
